import React, { useEffect, useState, useRef, useCallback } from 'react';
import { fetchPersonasSQL } from '../../services/General/personaService';
import { PersonaSQL } from '../../types/PersonaSQL';

interface PersonaSearchProps {
    onPersonaSelect?: (persona: PersonaSQL | null) => void;
    onPersonaNotFound?: (query: string) => void;
    value?: string;
    resetKey?: number;
    minChars?: number; // Caracteres mínimos para buscar (default: 3)
    debounceMs?: number; // Milisegundos de debounce (default: 400)
}

const PersonaSearch = ({
    onPersonaSelect,
    onPersonaNotFound,
    value = '',
    resetKey = 0,
    minChars = 3,
    debounceMs = 400,
}: PersonaSearchProps) => {
    const [query, setQuery] = useState(value);
    const [personas, setPersonas] = useState<PersonaSQL[]>([]);
    const [totalResultados, setTotalResultados] = useState(0);
    const [sugerencia, setSugerencia] = useState('');
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedPersona, setSelectedPersona] = useState<PersonaSQL | null>(null);
    const [activeSearchTerm, setActiveSearchTerm] = useState(''); // Término que se está buscando actualmente

    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const searchCounterRef = useRef(0); // Para tracking de secuencia

    // Reset cuando cambia resetKey
    useEffect(() => {
        setQuery('');
        setPersonas([]);
        setSelectedPersona(null);
        setIsOpen(false);
        setActiveSearchTerm('');
    }, [resetKey]);

    // Sincronizar con value prop (para cuando el padre actualiza externamente)
    useEffect(() => {
        setQuery(value);
    }, [value]);

    // Función de búsqueda con cancelación y validación de secuencia
    const performSearch = useCallback(async (searchText: string) => {
        // Incrementar contador para esta búsqueda
        const currentSearchId = ++searchCounterRef.current;
        
        // Cancelar solicitud anterior si existe
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Crear nuevo AbortController
        abortControllerRef.current = new AbortController();

        setActiveSearchTerm(searchText);
        setLoading(true);

        try {
            const response = await fetchPersonasSQL(searchText, {
                signal: abortControllerRef.current.signal
            });

            // Verificar que esta respuesta corresponde a la búsqueda más reciente
            if (currentSearchId === searchCounterRef.current) {
                console.log('Personas recibidas para término:', searchText, response);
                
                setPersonas(response.data || []);
                setTotalResultados(response.records || 0);
                setSugerencia(response.sugerencia || '');
            } else {
                console.log('Ignorando respuesta obsoleta para término:', searchText);
            }
        } catch (error: any) {
            // Ignorar errores de abort (son esperados)
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                console.log('Solicitud cancelada para término:', searchText);
                return;
            }
            console.error("Error buscando personas:", error);
        } finally {
            // Solo actualizar loading si esta es la búsqueda más reciente
            if (currentSearchId === searchCounterRef.current) {
                setLoading(false);
                setActiveSearchTerm('');
            }
        }
    }, []);

    // HandleSearch con debounce mejorado
    const handleSearch = useCallback((text: string) => {
        // Limpiar timeout anterior
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        // Si el texto es muy corto, limpiar resultados
        if (text.length < minChars) {
            setPersonas([]);
            setTotalResultados(0);
            setSugerencia('');
            setActiveSearchTerm('');
            return;
        }

        // Programar nueva búsqueda
        debounceRef.current = setTimeout(() => {
            performSearch(text);
        }, debounceMs);
    }, [minChars, debounceMs, performSearch]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setQuery(newValue);
        setSelectedPersona(null);
        setIsOpen(true);
        handleSearch(newValue);
    };

    const handleSelect = (persona: PersonaSQL) => {
        setSelectedPersona(persona);
        setQuery(`${persona.identificador} - ${persona.nombre} ${persona.apellido1}`);
        setIsOpen(false);
        setPersonas([]); // Limpiar resultados
        onPersonaSelect?.(persona);
        
        // Cancelar cualquier búsqueda pendiente
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
    };

    const handleClear = () => {
        setSelectedPersona(null);
        setQuery('');
        setPersonas([]);
        setTotalResultados(0);
        setSugerencia('');
        onPersonaSelect?.(null);
        
        // Cancelar búsquedas pendientes
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
    };

    // Limpiar al desmontar
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    // Determinar mensaje de estado
    const getStatusMessage = () => {
        if (loading && activeSearchTerm) {
            return (
                <div className="dropdown-item text-center text-muted">
                    <span className="spinner-border spinner-border-sm me-2" />
                    <small>Buscando "{activeSearchTerm}"...</small>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="position-relative">
            <div className="input-group input-group-sm">
                {/* <i className="bi bi-arrow-right-short d-block mb-2" ></i> */}
                <i className="bi bi-search d-block  mb-2" ></i>
                <input
                    type="text"
                    className="form-control ms-2"
                    placeholder={`Buscar por identificación o nombre (mínimo ${minChars} caracteres)...`}
                    value={query}
                    onChange={handleChange}
                    onFocus={() => setIsOpen(true)}
                    disabled={!!selectedPersona}
                />
                {selectedPersona && (
                    <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={handleClear}
                    >
                        ✕
                    </button>
                )}
            </div>

            {/* Dropdown */}
            {isOpen && !selectedPersona && (
                <div 
                    className="dropdown-menu show w-100 shadow-sm mt-1"
                    style={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        zIndex: 1050
                    }}
                >
                    {/* Estado de carga */}
                    {getStatusMessage()}

                    {/* Resultados */}
                    {!loading && personas.length > 0 && (
                        <>
                            {/* Cabecera informativa */}
                            {personas.length > 1 && (
                                <div className="px-3 py-2 small text-muted border-bottom">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Mostrando {personas.length} de {totalResultados} resultados
                                    {sugerencia && (
                                        <span className="text-primary ms-2">
                                            <i className="bi bi-lightbulb me-1"></i>
                                            {sugerencia}
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Lista de resultados */}
                            {personas.map((persona) => (
                                <button
                                    key={persona.identificador}
                                    type="button"
                                    className="dropdown-item small py-2"
                                    onMouseDown={() => handleSelect(persona)}
                                    style={{
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word',
                                        borderBottom: '1px solid #f0f0f0'
                                    }}
                                >
                                    <div className="fw-bold text-primary">
                                        {persona.identificador}
                                    </div>
                                    <div>
                                        {persona.nombre} {persona.apellido1} {persona.apellido2}
                                    </div>
                                    <small className="text-muted">
                                        <i className="bi bi-tag me-1"></i>
                                        {persona.tipo_identificacion}
                                    </small>
                                </button>
                            ))}
                        </>
                    )}

                    {/* Sin resultados */}
                    {!loading && personas.length === 0 && query.length >= minChars && (
                        <div className="dropdown-item text-center py-4">
                            <div className="text-muted mb-3 small">
                                <i className="bi bi-search fs-4 d-block mb-2"></i>
                                No se encontraron resultados para "{query}"
                            </div>
                            <button
                                className="btn btn-sm btn-primary"
                                onMouseDown={() => onPersonaNotFound?.(query)}
                            >
                                <i className="bi bi-person-plus me-1"></i>
                                Crear nueva persona
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PersonaSearch;